import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseTool, IToolContext, IToolComponentProps, IToolManifest } from '../../core/tools/interfaces/ITool';
import { Group, Ungroup } from 'lucide-react';
import { useDiagramStore } from '../../stores/diagramStore';
import { GroupNodesCommand } from '../../core/commands/GroupNodesCommand';
import { UngroupNodesCommand } from '../../core/commands/UngroupNodesCommand';
import { getNodeGroupKey, getNodeGroupPath, collectGroupMembersByKey, expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';
import type { ICommand } from '../../core/commands/ICommand';
import type { Node } from '../../core/models/scene';

const GroupToolPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const nodes = useDiagramStore((state) => state.nodes);
  const selectedNodeIds = useMemo(() => {
    const selected = Array.from(context.selection);
    return selected.filter((id) => nodes.some((n) => n.id === id));
  }, [context.selection, nodes]);

  const sharedGroupKey = useMemo(() => {
    if (selectedNodeIds.length < 2) return null;
    const keys = new Set<string>();
    selectedNodeIds.forEach((id) => {
      const node = nodes.find((n) => n.id === id);
      const key = getNodeGroupKey(node);
      if (!key) {
        keys.add('__ungrouped__');
        return;
      }
      keys.add(key);
    });
    if (keys.size !== 1) return null;
    const only = Array.from(keys)[0];
    return only === '__ungrouped__' ? null : only;
  }, [selectedNodeIds, nodes]);

  const canUngroup = selectedNodeIds.length >= 2 && !!sharedGroupKey;
  const canGroup = selectedNodeIds.length >= 2 && !canUngroup;

  return (
    canGroup || canUngroup ? (
      <div className="flex items-center gap-2">
        {canUngroup ? (
          <button
            type="button"
            data-testid="tid.tools.group.ungroup"
            className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
            aria-label={t('tools.ungroup', 'Ungroup')}
            onClick={() => onExecute({ action: 'ungroup' })}
          >
            <Ungroup className="h-4 w-4 text-foreground" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            data-testid="tid.tools.group.group"
            className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
            aria-label={t('tools.group', 'Group')}
            onClick={() => onExecute({ action: 'group' })}
          >
            <Group className="h-4 w-4 text-foreground" aria-hidden="true" />
          </button>
        )}
      </div>
    ) : null
  );
};

export class GroupTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'group',
    name: 'tools.group',
    icon: 'group',
    category: 'edit',
    description: 'Group or ungroup selected shapes',
    version: '1.0.0',

    requiresSelection: true,
    minSelection: 1,

    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: false,
      floatingPanel: true,
      sidebar: false,
      statusBar: false
    },

    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    },

    shortcuts: [
      { key: 'g', modifiers: ['ctrl'], description: 'Group selected shapes' },
      { key: 'g', modifiers: ['cmd'], description: 'Group selected shapes' }
    ]
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    const store = useDiagramStore.getState();
    const selectedIds = Array.from(context.selection);
    if (selectedIds.length < 2) return false;
    const hasNodes = selectedIds.some((id) => store.nodes.some((n) => n.id === id));
    const hasEdges = selectedIds.some((id) => store.edges.some((e) => e.id === id));
    return hasNodes && !hasEdges;
  }

  execute(params?: { action?: 'group' | 'ungroup' }): ICommand | null {
    if (!this.context) return null;
    const store = useDiagramStore.getState();
    const selectedIds = Array.from(this.context.selection);
    const selectedNodes = selectedIds
      .map((id) => store.nodes.find((n) => n.id === id))
      .filter((node): node is Node => Boolean(node));

    if (selectedNodes.length === 0) return null;

    const groupKeySet = new Set<string>();
    selectedNodes.forEach((node) => {
      const key = getNodeGroupKey(node);
      if (!key) {
        groupKeySet.add('__ungrouped__');
        return;
      }
      groupKeySet.add(key);
    });
    const sharedGroupKey = groupKeySet.size === 1
      ? (Array.from(groupKeySet)[0] === '__ungrouped__' ? null : Array.from(groupKeySet)[0])
      : null;
    const action = params?.action ?? (sharedGroupKey ? 'ungroup' : 'group');

    if (action === 'ungroup') {
      if (!sharedGroupKey) return null;
      const memberIds = collectGroupMembersByKey([sharedGroupKey], store.nodes);
      const updates: Array<{ nodeId: string; groupPath: string[] }> = [];
      memberIds.forEach((id) => {
        const node = store.nodes.find((n) => n.id === id);
        if (!node) return;
        const path = getNodeGroupPath(node);
        const nextPath = path.slice(0, Math.max(0, path.length - 1));
        updates.push({ nodeId: id, groupPath: nextPath });
      });

      if (updates.length === 0) return null;
      return new UngroupNodesCommand(updates);
    }

    if (selectedNodes.length < 2) return null;

    const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const expandedIds = expandNodeIdsWithGroupMembers(selectedNodes.map((n) => n.id), store.nodes);
    const updates = Array.from(expandedIds)
      .map((id) => store.nodes.find((n) => n.id === id))
      .filter((node): node is Node => Boolean(node))
      .map((node) => ({
        nodeId: node.id,
        groupPath: [...getNodeGroupPath(node), groupId],
      }));
    return new GroupNodesCommand(updates);
  }

  getToolbarComponent() {
    return GroupToolPanel;
  }

  getContextMenuComponent() {
    return GroupToolPanel;
  }

  getFloatingPanelComponent() {
    return GroupToolPanel;
  }
}
